import { anyType } from '@ardoq/global-consts';
import { omit } from 'lodash';
import { TraversalBuilderState } from '../types';

/**
 * Drops the start-set expansion and the synthetic "Context component type"
 * node it injected: the flag, the expansion state, the wildcard raw node and its
 * adjacency in the base graph.
 *
 * Only the wildcard-swap path creates that node, so a flag-only expansion clears
 * the flag alone. The metamodel is extended with the current traversal's triples,
 * which gives a wildcard traversal an `anyType` node and adjacency of its own -
 * deleting those here would take out state the expansion never owned.
 *
 * `startComponentType` is deliberately left alone. Callers either restore the
 * concrete type themselves or rely on the start-type switch detecting that the
 * current type is gone.
 *
 * Recursive collapsing rules go with it: they collapse the hierarchy the
 * expansion traverses, so without the expansion they would replace a reference
 * without hiding anything - a rule the builder would refuse to create.
 *
 * Lives on its own so both `applyStartSetExpansion` and `startContext` can use it
 * without importing each other.
 */
const withoutRecursiveCollapsingRules = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const { rules, currentEditableRule } = state.pathCollapsing;
  const recursiveRules = rules.filter(({ collapseRecursively }) =>
    Boolean(collapseRecursively)
  );
  if (
    recursiveRules.length === 0 &&
    !currentEditableRule?.collapseRecursively
  ) {
    return state;
  }
  return {
    ...state,
    highlightedPaths: omit(
      state.highlightedPaths,
      recursiveRules.map(({ referenceTypeId }) => referenceTypeId)
    ),
    pathCollapsing: {
      ...state.pathCollapsing,
      rules: rules.filter(({ collapseRecursively }) => !collapseRecursively),
      currentEditableRule: currentEditableRule?.collapseRecursively
        ? null
        : currentEditableRule,
    },
  };
};

export const withoutStartSetExpansion = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const withoutRules = withoutRecursiveCollapsingRules(state);
  if (!withoutRules.startSetExpansionState) {
    return { ...withoutRules, startSetExpansion: null };
  }

  const rawGraphNodes = new Map(withoutRules.rawGraphNodes);
  rawGraphNodes.delete(anyType);
  const sourceMap = new Map(withoutRules.baseGraph.sourceMap);
  sourceMap.delete(anyType);
  const targetMap = new Map(withoutRules.baseGraph.targetMap);
  targetMap.delete(anyType);

  return {
    ...withoutRules,
    startSetExpansion: null,
    startSetExpansionState: null,
    rawGraphNodes,
    baseGraph: { ...withoutRules.baseGraph, sourceMap, targetMap },
  };
};
